// whatsappInboxVmBuilder.js — monta o VM do painel de conversas do WhatsApp (shell/whatsappInbox.vm.js @v3) a partir
// do JSON da API (GET /v1/me/whatsapp/conversations e …/{key}/messages — §5.5 de
// docs/PLANO-EXECUCAO-CONVERSAS-WHATSAPP.md, com a revisão 2, e o `composer` da §5.4 de
// docs/PLANO-EXECUCAO-RESPONDER-CLIENTE-WHATSAPP.md). Puro: sem fetch, sem estado — testado em
// __tests__/whatsappInboxVmBuilder.test.js com o JSON literal do contrato.
//
// Horários em BRASÍLIA (formatTime já fixa America/Sao_Paulo); "Hoje/Ontem" também pelo dia de Brasília.

import { formatTime } from '../utils/formatters';
import { maskPhoneBR } from '../utils/masks';

const BRT = 'America/Sao_Paulo';
const REPLY_PREVIEW_MAX = 120;

// 'aaaa-mm-dd' do instante no fuso de Brasília (en-CA formata exatamente assim).
const diaEmBrasilia = (date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: BRT, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

/** '2' | '9+' | null quando 0 — o número do sino e de cada conversa. */
export function unreadLabel(n) {
  const k = Number(n) || 0;
  if (k <= 0) return null;
  return k > 9 ? '9+' : String(k);
}

/** '(38) 99974-2453' para número brasileiro; número de fora fica como veio. */
export function formatPhone(e164) {
  if (!e164) return '';
  return String(e164).startsWith('+55') ? maskPhoneBR(e164) : String(e164);
}

/** 'MS' para o avatar (1ª letra do primeiro e do último nome); null quando não há nome (avatar genérico). */
export function initialsFrom(name) {
  const palavras = String(name ?? '').trim().split(/\s+/).filter((p) => /\p{L}/u.test(p));
  if (palavras.length === 0) return null;
  const primeira = palavras[0][0];
  const ultima = palavras.length > 1 ? palavras[palavras.length - 1][0] : '';
  return `${primeira}${ultima}`.toUpperCase();
}

/** 'https://wa.me/5538999742453' — abre a conversa com o cliente no WhatsApp do dono. null sem telefone. */
export function whatsAppHref(e164) {
  const digitos = String(e164 ?? '').replace(/\D/g, '');
  return digitos.length >= 10 ? `https://wa.me/${digitos}` : null;
}

/** 'Hoje' | 'Ontem' | '20/10/2026' — separador de dias da conversa (dia de Brasília). */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = diaEmBrasilia(d);
  if (dia === diaEmBrasilia(now)) return 'Hoje';
  if (dia === diaEmBrasilia(new Date(now.getTime() - 86_400_000))) return 'Ontem';   // Brasil sem horário de verão
  const [a, m, dd] = dia.split('-');
  return `${dd}/${m}/${a}`;
}

/** 'Hoje 14:11' | 'Ontem' | '20/10' — horário da última mensagem na lista de conversas. */
export function lastTimeLabel(iso, now = new Date()) {
  const dia = dayLabel(iso, now);
  if (dia === 'Hoje') return `Hoje ${formatTime(iso)}`;
  if (dia === 'Ontem' || !dia) return dia;
  return dia.slice(0, 5);
}

/** '23/10 às 14:30' (Brasília) — quando é o agendamento. */
export function appointmentWhenLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const [, m, dd] = diaEmBrasilia(d).split('-');
  return `${dd}/${m} às ${formatTime(iso)}`;
}

const preview = (text) => {
  if (text == null) return null;
  const t = String(text).replace(/\s+/g, ' ').trim();
  return t.length > REPLY_PREVIEW_MAX ? `${t.slice(0, REPLY_PREVIEW_MAX - 1).trimEnd()}…` : t;
};

function buildAppointment(a) {
  if (!a) return null;
  const partes = [a.serviceName, a.professionalName].filter(Boolean);
  return {
    label: partes.length === 2 ? `${partes[0]} com ${partes[1]}` : (partes[0] || 'Agendamento'),
    whenLabel: appointmentWhenLabel(a.startTime),
    statusLabel: a.statusLabel,
    status: a.status,
  };
}

/** Item de mensagem da API → InboxMessage. O texto e o rótulo passam como vieram (a API já traduziu). */
export function buildMessage(m, now = new Date()) {
  return {
    id: m.id,
    direction: m.direction === 'in' ? 'in' : 'out',
    kind: m.kind,
    label: m.label,
    text: m.text ?? null,            // null = sem texto (antiga, áudio, foto…) → a View mostra só o rótulo
    timeLabel: formatTime(m.createdAt),
    dayLabel: dayLabel(m.createdAt, now),
    unread: m.direction === 'in' && !!m.unread,   // só recebida "grita"
    delivery: m.delivery ?? null,
    buttons: Array.isArray(m.buttons) ? m.buttons : [],
    replyTo: m.replyTo
      ? { direction: m.replyTo.direction === 'in' ? 'in' : 'out', label: m.replyTo.label, text: preview(m.replyTo.text) }
      : null,
    appointment: buildAppointment(m.appointment),
  };
}

/** Item da lista de conversas da API → InboxConversation. */
export function buildConversation(c, now = new Date()) {
  const phoneLabel = formatPhone(c.phone);
  return {
    key: c.key,
    title: c.displayName || phoneLabel || 'Cliente',
    initials: initialsFrom(c.displayName),
    phoneLabel,
    personHref: c.personId ? `/persons/${c.personId}` : null,
    replyHref: whatsAppHref(c.phone),
    unreadCount: Number(c.unread) || 0,
    unreadLabel: unreadLabel(c.unread),
    lastMessage: buildMessage(c.lastMessage, now),
    lastTimeLabel: lastTimeLabel(c.lastMessage?.createdAt, now),
  };
}

/** Itens de GET /conversations → InboxConversation[] (ordem da API: mais recente primeiro). */
export function buildConversations(items, now = new Date()) {
  return (Array.isArray(items) ? items : items?.items ?? []).map((c) => buildConversation(c, now));
}

// ── A caixa de escrever (@v3) ─────────────────────────────────────────────

const COUNTER_FROM = 1200;   // o contador só aparece perto do limite — antes é ruído
const NUMERO_BR = new Intl.NumberFormat('pt-BR');   // '1.500' — número, não data (o lint de fuso barra toLocaleString)
const ptBR = (n) => NUMERO_BR.format(Number(n));

const ABRIR_NO_MEU_WHATSAPP = 'Abrir no meu WhatsApp';

// Textos do bloqueio por `reason` da API (tabela do §5.5 do plano RESPONDER-CLIENTE).
const BLOQUEIOS = {
  client_never_wrote: {
    title: 'O cliente ainda não escreveu',
    text: 'O WhatsApp só deixa você escrever aqui depois que o cliente mandar uma mensagem. Para falar antes, use o seu WhatsApp.',
    action: 'replyOnWhatsApp',
  },
  window_closed: {
    title: 'O prazo para responder acabou',
    text: 'O WhatsApp só deixa responder até 24 horas depois da última mensagem do cliente. Para continuar, use o seu WhatsApp.',
    action: 'replyOnWhatsApp',
  },
  no_credit: {
    title: 'Sem créditos',
    text: 'Cada mensagem enviada ou recebida usa 1 crédito. Compre um pacote para responder.',
    action: 'buyCredits',
  },
  whatsapp_disabled: {
    title: 'WhatsApp desligado',
    text: 'Ligue o WhatsApp no menu WhatsApp para responder.',
    action: null,
  },
};

const ROTULO_DA_ACAO = { replyOnWhatsApp: ABRIR_NO_MEU_WHATSAPP, buyCredits: 'Comprar créditos' };

/** Regras de envio do ícone (?) — pedido do André (D3). O limite vem da API. */
export function composerRules(maxLength = 1500) {
  return {
    title: 'Regras de envio',
    items: [
      `Até ${ptBR(maxLength)} caracteres por mensagem.`,
      'Você pode responder até 24 horas depois da última mensagem do cliente. Cada nova mensagem dele renova o prazo. Depois disso, só pelo seu WhatsApp.',
      'Cada mensagem custa 1 crédito — a que você envia e a que o cliente manda. Prefira uma mensagem completa a várias curtas seguidas.',
      'Só texto por enquanto: foto, áudio e documento ainda não.',
      'A mensagem vai assinada com o nome do seu estabelecimento, porque o cliente vê a conversa no número do Agendário.',
      'Depois de enviada, a mensagem não pode ser apagada nem editada.',
    ],
  };
}

/** 'Você pode responder até hoje às 23:50' | '… até amanhã às 09:50' | '… até 24/10 às 09:50' — dia de Brasília. */
export function windowLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  const dia = diaEmBrasilia(d);
  const quando = dia === diaEmBrasilia(now) ? 'hoje'
    : dia === diaEmBrasilia(new Date(now.getTime() + 86_400_000)) ? 'amanhã'   // Brasil sem horário de verão
      : (() => { const [, m, dd] = dia.split('-'); return `${dd}/${m}`; })();
  return `Você pode responder até ${quando} às ${formatTime(iso)}`;
}

/**
 * `composer` da API + o estado da caixa (o que está digitado, enviando, erro) → InboxComposer.
 * `api` null (conversa que não é do dono, ou ainda carregando) → null: a View não desenha a caixa.
 */
export function buildComposer(api, { text = '', sending = false, errorMessage = null, replyHref = null, now = new Date() } = {}) {
  if (!api) return null;
  const maxLength = Number(api.maxLength) || 1500;
  const digitado = String(text ?? '');

  let blocked = null;
  if (!api.canSend) {
    const b = BLOQUEIOS[api.reason] ?? { title: 'Não dá para responder agora', text: 'Tente de novo em instantes.', action: null };
    // Sem telefone não há como abrir o WhatsApp do dono: o bloqueio fica só com a explicação.
    const action = b.action === 'replyOnWhatsApp' && !replyHref ? null : b.action;
    blocked = { title: b.title, text: b.text, action, actionLabel: action ? ROTULO_DA_ACAO[action] : null };
  }

  return {
    canSend: !!api.canSend,
    text: digitado,
    maxLength,
    counterLabel: digitado.length >= COUNTER_FROM ? `${ptBR(digitado.length)}/${ptBR(maxLength)}` : null,
    placeholder: 'Escreva uma mensagem',
    signatureLabel: api.signature ? `Vai assinada como *${api.signature}*` : null,
    costLabel: 'Cada mensagem enviada ou recebida usa 1 crédito',
    windowLabel: api.canSend ? windowLabel(api.windowEndsAt, now) : null,
    blocked,
    sending: !!sending,
    errorMessage: errorMessage ?? null,
    rules: composerRules(maxLength),
  };
}

/**
 * Estado completo do painel.
 * - `conversationItems` = conversas já acumuladas (todas as páginas de "carregar mais"), na forma da API.
 * - `messages` = mensagens já acumuladas da conversa aberta (mais antiga → mais nova), na forma da API.
 * - `composer` = o `composer` da API (GET …/messages); `composerText`/`sending`/`sendError` = estado da caixa.
 *
 * @returns {import('../shell/whatsappInbox.vm').WhatsAppInboxVM}
 */
export function buildInboxVm({
  open = false,
  conversationItems = null,
  conversationsLoading = false,
  conversationsError = false,
  hasMoreConversations = false,
  loadingMoreConversations = false,
  searchQuery = '',
  selectedKey = null,
  messages = null,
  hasMore = false,
  messagesLoading = false,
  loadingMore = false,
  messagesError = false,
  composer = null,
  composerText = '',
  sending = false,
  sendError = null,
  now = new Date(),
} = {}) {
  const conversations = buildConversations(conversationItems ?? [], now);
  const base = {
    open,
    conversations,
    searchQuery: searchQuery ?? '',
    hasMoreConversations: !!hasMoreConversations,
    loadingMoreConversations: !!loadingMoreConversations,
    conversation: null,
    errorMessage: null,
  };

  if (selectedKey) {
    if (messagesError) {
      return { ...base, state: 'error', errorMessage: 'Não foi possível carregar a conversa. Tente novamente.' };
    }
    if (messagesLoading || !messages) return { ...base, state: 'loading' };

    const selecionada = conversations.find((c) => c.key === selectedKey);
    const phoneLabel = selecionada?.phoneLabel ?? '';
    const replyHref = selecionada?.replyHref ?? null;
    return {
      ...base,
      state: 'conversation',
      conversation: {
        key: selectedKey,
        title: selecionada?.title || phoneLabel || 'Cliente',
        initials: selecionada?.initials ?? null,
        phoneLabel,
        personHref: selecionada?.personHref ?? null,
        replyHref,
        messages: messages.map((m) => buildMessage(m, now)),
        hasMore: !!hasMore,
        loadingMore: !!loadingMore,
        composer: buildComposer(composer, { text: composerText, sending, errorMessage: sendError, replyHref, now }),
      },
    };
  }

  if (conversationsError) {
    return { ...base, state: 'error', errorMessage: 'Não foi possível carregar as conversas. Tente novamente.' };
  }
  if (conversationsLoading && !conversationItems) return { ...base, state: 'loading' };
  if (conversations.length === 0) return { ...base, state: (searchQuery ?? '').trim() ? 'noResults' : 'empty' };
  return { ...base, state: 'list' };
}

/**
 * Junta a página mais nova da conversa aberta com o que já está na tela (atualização a cada 20 s): mensagem nova
 * entra, mensagem que já estava é TROCADA pela nova versão (o tique muda de "Entregue" para "Lida"), e a ordem fica
 * cronológica. Puro — o container chama com o que tem.
 */
export function mergeMessages(atual, novas) {
  const porId = new Map((atual ?? []).map((m) => [m.id, m]));
  for (const m of novas ?? []) porId.set(m.id, m);
  return [...porId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}
